import React, { useState, useEffect, useContext } from 'react';
import { apiFetch } from "../context/AuthContext";
import {
    Users,
    Plus,
    Search,
    UserPlus,
    X,
    AlertCircle,
    CheckCircle,
    Shield,
    Calendar,
    Trash2
} from 'lucide-react';

// Import theme context for dark/light mode
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";

// Responsive helper functions
const getScreenSize = () => {
    const width = window.innerWidth;
    if (width <= 480) return "xs"; 
    if (width <= 768) return "sm";
    if (width <= 1024) return "md";
    if (width <= 1366) return "lg";
    if (width <= 1920) return "xl";
    return "xxl";
};

const getResponsiveSpacing = (screenSize) => {
    const spacingMap = {
        xs: { padding: "0.5rem", margin: "0.25rem", gap: "0.5rem" },
        sm: { padding: "0.75rem", margin: "0.5rem", gap: "0.75rem" },
        md: { padding: "1rem", margin: "0.75rem", gap: "1rem" },
        lg: { padding: "1.25rem", margin: "1rem", gap: "1.25rem" },
        xl: { padding: "1.5rem", margin: "1.25rem", gap: "1.5rem" },
        xxl: { padding: "2rem", margin: "1.5rem", gap: "2rem" },
    };
    return spacingMap[screenSize] || spacingMap.lg;
};

const getResponsiveFontSizes = (screenSize) => {
    const fontMap = {
        xs: { title: "1.25rem", subtitle: "0.875rem", body: "0.75rem", small: "0.6875rem" },
        sm: { title: "1.5rem", subtitle: "1rem", body: "0.875rem", small: "0.75rem" },
        md: { title: "1.75rem", subtitle: "1.125rem", body: "0.9375rem", small: "0.8125rem" },
        lg: { title: "2rem", subtitle: "1.25rem", body: "1rem", small: "0.875rem" },
        xl: { title: "2.25rem", subtitle: "1.375rem", body: "1rem", small: "0.875rem" },
        xxl: { title: "2.5rem", subtitle: "1.5rem", body: "1.125rem", small: "1rem" },
    };
    return fontMap[screenSize] || fontMap.lg;
};

function GroupsManagement() {
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    const { theme } = useContext(ThemeContext);
    const { language } = useContext(LanguageContext);

    const [screenSize, setScreenSize] = useState(getScreenSize());
    const [spacing, setSpacing] = useState(getResponsiveSpacing(screenSize));
    const [fonts, setFonts] = useState(getResponsiveFontSizes(screenSize));

    const [groups, setGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [newGroupName, setNewGroupName] = useState("");
    const [employeeIds, setEmployeeIds] = useState("");
    const [availableEmployees, setAvailableEmployees] = useState([]);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [userRole, setUserRole] = useState("");
    const [createdGroups, setCreatedGroups] = useState([]);
    const [activeTab, setActiveTab] = useState("member");

    const text = {
        en: {
            title: "Groups Management",
            subtitle: "Manage groups and assign employees",
            myGroups: "My Groups",
            allGroups: "All Groups",
            createGroup: "Create Group",
            assignEmployees: "Assign Employees",
            groupName: "Group Name",
            creator: "Creator",
            actions: "Actions",
            noGroups: "No groups found",
            noGroupsMessage: "You are not a member of any groups yet",
            createGroupTitle: "Create New Group",
            enterGroupName: "Enter group name",
            assignEmployeesTitle: "Assign Employees to Group",
            employeeIds: "Employee IDs",
            employeeIdsPlaceholder: "Enter employee IDs (comma-separated, e.g., 11,12,13)",
            cancel: "Cancel",
            create: "Create",
            assign: "Assign",
            loading: "Loading...",
            search: "Search groups...",
            adminOnly: "Admin/Manager Only",
            groupCreated: "Group created successfully",
            employeesAssigned: "Employees assigned successfully",
            failedToCreate: "Failed to create group",
            failedToAssign: "Failed to assign employees",
            unauthorized: "You must be an admin or manager to perform this action",
            memberOfGroups: "Member Of Groups", // ADD
            createdGroups: "Created Groups", // ADD
            groupNameIsRequred: "Group name is required",
            noCreatedGroups: "No created groups", // ADD
            noCreatedGroupsMessage: "You haven't created any groups yet", // ADD
            createdAt: "Created At", // ADD
            selectEmployees: "Select Employees",
            selectEmployeesHint: "Hold Ctrl/Cmd to select multiple employees",
            selectGroup: "Select Group",
            chooseGroup: "Choose a group...",
            atLeastOneEmployee: "Group and at least one employee are required",
        },
        ar: {
            title: "إدارة المجموعات",
            subtitle: "إدارة المجموعات وتعيين الموظفين",
            myGroups: "مجموعاتي",
            allGroups: "جميع المجموعات",
            createGroup: "إنشاء مجموعة",
            assignEmployees: "تعيين موظفين",
            groupName: "اسم المجموعة",
            creator: "المنشئ",
            actions: "الإجراءات",
            noGroups: "لم يتم العثور على مجموعات",
            noGroupsMessage: "أنت لست عضوًا في أي مجموعات حتى الآن",
            createGroupTitle: "إنشاء مجموعة جديدة",
            enterGroupName: "أدخل اسم المجموعة",
            assignEmployeesTitle: "تعيين موظفين للمجموعة",
            employeeIds: "معرفات الموظفين",
            employeeIdsPlaceholder: "أدخل معرفات الموظفين (مفصولة بفواصل، مثال: 11,12,13)",
            cancel: "إلغاء",
            create: "إنشاء",
            assign: "تعيين",
            loading: "جاري التحميل...",
            search: "البحث في المجموعات...",
            adminOnly: "للمسؤولين/المديرين فقط",
            groupCreated: "تم إنشاء المجموعة بنجاح",
            employeesAssigned: "تم تعيين الموظفين بنجاح",
            failedToCreate: "فشل في إنشاء المجموعة",
            failedToAssign: "فشل في تعيين الموظفين",
            unauthorized: "يجب أن تكون مسؤولاً أو مديرًا لتنفيذ هذا الإجراء",
            memberOfGroups: "عضو في المجموعات",
            createdGroups: "المجموعات المنشأة",
            groupNameIsRequred: "برجاء كتابة اسم المجموعه",
            noCreatedGroups: "لا توجد مجموعات منشأة",
            noCreatedGroupsMessage: "لم تقم بإنشاء أي مجموعات بعد",
            createdAt: "تاريخ الإنشاء",
            selectEmployees: "اختر الموظفين",
            selectEmployeesHint: "اضغط Ctrl/Cmd لاختيار موظفين متعددين",
            selectGroup: "اختر المجموعة",
            chooseGroup: "اختر مجموعة...",
            atLeastOneEmployee: "المجموعة وموظف واحد على الأقل مطلوبان",
        }
    };

    useEffect(() => {
        const handleResize = () => {
            const newScreenSize = getScreenSize();
            setScreenSize(newScreenSize);
            setSpacing(getResponsiveSpacing(newScreenSize));
            setFonts(getResponsiveFontSizes(newScreenSize));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const role = localStorage.getItem("role");
        setUserRole(role);
        fetchMemberOfGroups();
        if (role === "admin" || role === "manager") {
            fetchAllGroups();
        }
    }, []);

    useEffect(() => {
        if (activeTab === "member") {
            fetchMemberOfGroups();
        } else if (activeTab === "created" && (userRole === "admin" || userRole === "manager")) {
            fetchCreatedGroups();
        }
    }, [activeTab]);

    const fetchMemberOfGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/member-of-groups`, { method: "GET" });
            const data = await res.json();
            setGroups(data || []);
        } catch (err) {
            console.error('Error fetching my groups:', err);
            setError("Failed to load groups");
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableEmployees = async () => {
        try {
            const headers = {};
            if (userRole === "manager") {
                headers['x-view-type'] = 'manager';
            }

            const res = await apiFetch(`${apiBaseUrl}/employees/visibility`, {
                method: "GET",
                headers
            });
            const data = await res.json();
            setAvailableEmployees(data.employees || []);
        } catch (err) {
            console.error('Error fetching employees:', err);
        }
    };

    const fetchAllGroups = async () => {
        try {
            const res = await apiFetch(`${apiBaseUrl}/api/groups/`, { method: "GET" });
            const data = await res.json();
            setAllGroups(data || []);
        } catch (err) {
            console.error('Error fetching all groups:', err);
        }
    };

    const fetchCreatedGroups = async () => {
        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups/created-groups`, { method: "GET" });
            const data = await res.json();
            setCreatedGroups(data || []);
        } catch (err) {
            console.error('Error fetching created groups:', err);
            setError("Failed to load created groups");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async () => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!newGroupName.trim()) {
            setError(text[language].groupNameIsRequred)
            return;
        }

        try {
            setLoading(true);
            const res = await apiFetch(`${apiBaseUrl}/api/groups`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ groupName: newGroupName })
            });

            if (!res.ok) throw new Error("Failed to create group");

            setSuccess(text[language].groupCreated);
            setShowCreateModal(false);
            setNewGroupName("");
            fetchMemberOfGroups();
            if (userRole === "admin" || userRole === "manager") {
                fetchAllGroups();
            }
        } catch (err) {
            setError(text[language].failedToCreate);
        } finally {
            setLoading(false);
        }
    };

    const handleAssignEmployees = async () => {
        if (userRole !== "admin" && userRole !== "manager") {
            setError(text[language].unauthorized);
            return;
        }

        if (!selectedGroup || selectedEmployeeIds.length === 0) {
            setError(text[language].atLeastOneEmployee);
            return;
        }

        try {
            setLoading(true);
            const empIds = selectedEmployeeIds.map(id => parseInt(id));

            const res = await apiFetch(`${apiBaseUrl}/api/groups/${selectedGroup.groupId}/members`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ empIds })
            });

            if (!res.ok) throw new Error("Failed to assign employees");

            setSuccess(text[language].employeesAssigned);
            setShowAssignModal(false);
            setSelectedEmployeeIds([]);
            setSelectedGroup(null);
        } catch (err) {
            setError(text[language].failedToAssign);
        } finally {
            setLoading(false);
        }
    };

    const getFilteredGroups = () => {
        const dataSource = activeTab === "member" ? groups : createdGroups;
        return dataSource.filter(group =>
            group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (group.creatorSr && group.creatorSr.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    };

    const styles = {
        container: {
            direction: language === "ar" ? "rtl" : "ltr",
        },
        header: {
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
            borderRadius: screenSize === "xs" ? "1rem" : "1.5rem",
            padding: spacing.padding,
            marginBottom: spacing.margin,
            boxShadow: theme === "dark"
                ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        },
        headerContent: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: screenSize === "xs" ? "flex-start" : "center",
            flexDirection: screenSize === "xs" ? "column" : "row",
            gap: spacing.gap,
        },
        title: {
            color: theme === "dark" ? "white" : "#1a1a1a",
            fontSize: "28px",
            fontWeight: "700",
            margin: 0,
            marginBottom: "0.5rem",
        },
        subtitle: {
            color: theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
            fontSize: fonts.subtitle,
            margin: 0,
        },
        buttonGroup: {
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap",
        },
        createBtn: {
            background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
            color: "white",
            border: "none",
            padding: screenSize === "xs" ? "0.75rem 1rem" : "0.75rem 1.5rem",
            borderRadius: "0.75rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            transition: "all 0.3s ease",
            boxShadow: "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)",
        },
        searchContainer: {
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
            padding: spacing.padding,
            borderRadius: "1rem",
            marginBottom: spacing.margin,
            boxShadow: theme === "dark"
                ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        },
        searchInput: {
            background: theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "white",
            border: theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "0.5rem",
            padding: screenSize === "xs" ? "0.625rem 0.75rem" : "0.75rem 1rem",
            color: theme === "dark" ? "white" : "#1a1a1a",
            fontSize: fonts.body,
            outline: "none",
            width: "100%",
        },
        groupsGrid: {
            display: "grid",
            gridTemplateColumns: screenSize === "xs"
                ? "1fr"
                : screenSize === "sm"
                    ? "repeat(2, 1fr)"
                    : "repeat(3, 1fr)",
            gap: spacing.gap,
        },
        groupCard: {
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            padding: "1.25rem",
            borderRadius: "1rem",
            boxShadow: theme === "dark"
                ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                : "0 0.5rem 2rem rgba(0,0,0,0.1)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
        },
        tabContainer: {
            display: "flex",
            gap: spacing.gap,
            marginBottom: spacing.margin,
            borderBottom: theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.1)",
        },
        tab: {
            background: "transparent",
            border: "none",
            padding: screenSize === "xs" ? "0.75rem 1rem" : "1rem 1.5rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            color: theme === "dark" ? "rgba(255, 255, 255, 0.6)" : "rgba(26, 26, 26, 0.6)",
            borderBottom: "2px solid transparent",
            transition: "all 0.3s ease",
        },
        activeTab: {
            color: "#2e7cba",
            borderBottomColor: "#2e7cba",
        },
        modal: {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: screenSize === "xs" ? "1rem" : "2rem",
        },
        modalContent: {
            background: theme === "dark"
                ? "rgba(30, 30, 30, 0.98)"
                : "rgba(255, 255, 255, 0.98)",
            borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
            padding: screenSize === "xs" ? "1.5rem" : "2rem",
            width: screenSize === "xs" ? "100%" : "90%",
            maxWidth: "500px",
            boxShadow: theme === "dark"
                ? "0 20px 60px rgba(0, 0, 0, 0.8)"
                : "0 20px 60px rgba(0, 0, 0, 0.3)",
        },
        formGroup: {
            marginBottom: spacing.margin,
        },
        formLabel: {
            display: "block",
            color: theme === "dark" ? "rgba(255, 255, 255, 0.8)" : "rgba(26, 26, 26, 0.7)",
            fontSize: fonts.body,
            fontWeight: "500",
            marginBottom: "0.5rem",
        },
        formInput: {
            background: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(255, 255, 255, 0.9)",
            border: theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "0.5rem",
            padding: "0.75rem",
            color: theme === "dark" ? "white" : "#1a1a1a",
            fontSize: fonts.body,
            outline: "none",
            width: "100%",
        },
        formSelect: {
            appearance: "none",
            background: theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(255, 255, 255, 0.95)",
            border: theme === "dark"
                ? "1px solid rgba(255,255,255,0.15)"
                : "1px solid rgba(148, 163, 184, 0.2)",
            borderRadius: "0.75rem",
            padding: screenSize === "xs" ? "0.75rem" : "0.625rem 2rem 0.625rem 0.875rem",
            color: theme === "dark"
                ? "rgba(248, 250, 252, 0.95)"
                : "rgba(30, 41, 59, 0.9)",
            fontSize: fonts.body,
            outline: "none",
            width: "100%",
            cursor: "pointer",
            transition: "all 0.3s ease",
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${theme === "dark" ? "%23d1d5db" : "%23374151"
                }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: language === "ar" ? "left 0.75rem center" : "right 0.75rem center",
        },
        submitBtn: {
            background: "linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)",
            color: "white",
            border: "none",
            padding: "0.875rem 2rem",
            borderRadius: "0.75rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            transition: "all 0.3s ease",
            boxShadow: "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)",
            width: "100%",
        },
        cancelBtn: {
            background: theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
            color: theme === "dark" ? "white" : "#1a1a1a",
            border: theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.2)"
                : "1px solid rgba(0, 0, 0, 0.1)",
            padding: "0.875rem 2rem",
            borderRadius: "0.75rem",
            cursor: "pointer",
            fontSize: fonts.body,
            fontWeight: "600",
            transition: "all 0.3s ease",
            width: "100%",
        },
        emptyState: {
            textAlign: "center",
            padding: screenSize === "xs" ? "3rem 1rem" : "4rem 2rem",
            color: theme === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(26, 26, 26, 0.5)",
        },
    };

    const Alert = ({ type, message, onClose }) => {
        useEffect(() => {
            const timer = setTimeout(() => onClose(), 5000);
            return () => clearTimeout(timer);
        }, [onClose]);

        return (
            <div
                style={{
                    position: "fixed",
                    top: "1.25rem",
                    right: "1.25rem",
                    zIndex: 3000,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "0.5rem",
                    border: "1px solid",
                    maxWidth: "25rem",
                    backdropFilter: "blur(10px)",
                    backgroundColor: type === "error"
                        ? "rgba(239, 68, 68, 0.1)"
                        : "rgba(34, 197, 94, 0.1)",
                    borderColor: type === "error" ? "#ef4444" : "#22c55e",
                    color: type === "error" ? "#ef4444" : "#22c55e",
                }}
            >
                {type === "error" ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                <span style={{ fontSize: fonts.body, flex: 1 }}>{message}</span>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        color: "inherit",
                        cursor: "pointer",
                        padding: "0.25rem",
                    }}
                >
                    <X size={14} />
                </button>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {error && <Alert type="error" message={error} onClose={() => setError("")} />}
            {success && <Alert type="success" message={success} onClose={() => setSuccess("")} />}

            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerContent}>
                    <div>
                        <h1 style={styles.title}>
                            <Users size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                            {text[language].title}
                        </h1>
                        {(userRole === "admin" || userRole === "manager") && <p style={styles.subtitle}>{text[language].subtitle}</p>}
                    </div>

                    <div style={styles.buttonGroup}>
                        {(userRole === "admin" || userRole === "manager") && (
                            <>
                                <button
                                    style={styles.createBtn}
                                    onClick={() => setShowCreateModal(true)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 0.5rem 1.5rem rgba(46, 124, 186, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(46, 124, 186, 0.3)";
                                    }}
                                >
                                    <Plus size={20} />
                                    {text[language].createGroup}
                                </button>

                                <button
                                    style={{ ...styles.createBtn, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                                    onClick={() => {
                                        fetchAvailableEmployees();
                                        setShowAssignModal(true);
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = "0 0.5rem 1.5rem rgba(16, 185, 129, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(16, 185, 129, 0.3)";
                                    }}
                                >
                                    <UserPlus size={20} />
                                    {text[language].assignEmployees}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Search */}
            <div style={styles.searchContainer}>
                <div style={{ position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }} />
                    <input
                        type="text"
                        placeholder={text[language].search}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ ...styles.searchInput, paddingLeft: '3rem' }}
                    />
                </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{
                        ...styles.tab,
                        ...(activeTab === "member" ? styles.activeTab : {}),
                    }}
                    onClick={() => setActiveTab("member")}
                >
                    {text[language].memberOfGroups}
                </button>
                {(userRole === "admin" || userRole === "manager") && (
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === "created" ? styles.activeTab : {}),
                        }}
                        onClick={() => setActiveTab("created")}
                    >
                        {text[language].createdGroups}
                    </button>
                )}
            </div>

            {/* Groups Grid */}
            {loading ? (
                <div style={styles.emptyState}>
                    <div style={{ fontSize: fonts.subtitle }}>{text[language].loading}</div>
                </div>
            ) : getFilteredGroups().length === 0 ? (
                <div style={styles.emptyState}>
                    <Users size={64} style={{ opacity: 0.3, marginBottom: "1rem" }} />
                    <div style={{ fontSize: fonts.subtitle, fontWeight: "600" }}>
                        {activeTab === "member" ? text[language].noGroups : text[language].noCreatedGroups}
                    </div>
                    <div style={{ fontSize: fonts.body, marginTop: "0.5rem" }}>
                        {activeTab === "member" ? text[language].noGroupsMessage : text[language].noCreatedGroupsMessage}
                    </div>
                </div>
            ) : (
                <div style={styles.groupsGrid}>
                    {getFilteredGroups().map((group) => (
                        <div
                            key={group.groupId || group.id}
                            style={styles.groupCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = theme === "dark"
                                    ? "0 0.75rem 2.5rem rgba(0,0,0,0.4)"
                                    : "0 0.75rem 2.5rem rgba(0,0,0,0.15)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = theme === "dark"
                                    ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
                                    : "0 0.5rem 2rem rgba(0,0,0,0.1)";
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #2e7cba 0%, #1e5a8e 100%)',
                                    padding: '0.75rem',
                                    borderRadius: '0.75rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Users size={24} color="white" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ margin: 0, fontSize: fonts.subtitle, color: theme === "dark" ? "white" : "#1a1a1a" }}>
                                        {group.groupName}
                                    </h3>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: fonts.small }}>
                                {activeTab === "member" ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                                        <Shield size={14} />
                                        <span>Creator: {group.creatorSr}</span>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                                            <Shield size={14} />
                                            <span>ID: {group.id}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.7 }}>
                                            <Calendar size={14} />
                                            <span>Created: {new Date(group.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Group Modal */}
            {showCreateModal && (
                <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.margin }}>
                            <h2 style={{ margin: 0, fontSize: fonts.subtitle }}>{text[language].createGroupTitle}</h2>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>{text[language].groupName}</label>
                            <input
                                type="text"
                                value={newGroupName}
                                onChange={(e) => setNewGroupName(e.target.value)}
                                placeholder={text[language].enterGroupName}
                                style={styles.formInput}
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.gap }}>
                            <button onClick={() => setShowCreateModal(false)} style={styles.cancelBtn}>
                                {text[language].cancel}
                            </button>
                            <button onClick={handleCreateGroup} style={styles.submitBtn} disabled={loading}>
                                {loading ? text[language].loading : text[language].create}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Employees Modal */}
            {showAssignModal && (
                <div style={styles.modal} onClick={() => setShowAssignModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.margin }}>
                            <h2 style={{ margin: 0, fontSize: fonts.subtitle }}>{text[language].assignEmployeesTitle}</h2>
                            <button
                                onClick={() => setShowAssignModal(false)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>{text[language].selectGroup}</label>
                            <select
                                value={selectedGroup?.groupId || ''}
                                onChange={(e) => {
                                    const group = (userRole === "admin" || userRole === "manager" ? allGroups : groups).find(g => g.groupId === e.target.value);
                                    setSelectedGroup(group);
                                }}
                                style={styles.formSelect}
                            >
                                <option value="">{text[language].chooseGroup}</option>
                                {(userRole === "admin" || userRole === "manager" ? allGroups : groups).map(group => (
                                    <option key={group.groupId} value={group.groupId}>{group.groupName}</option>
                                ))}
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>{text[language].selectEmployees}</label>
                            <select
                                multiple
                                value={selectedEmployeeIds}
                                onChange={(e) => {
                                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                                    setSelectedEmployeeIds(selected);
                                }}
                                style={{
                                    ...styles.formSelect,
                                    height: '200px',
                                    backgroundImage: 'none'
                                }}
                            >
                                {availableEmployees.map(emp => (
                                    <option key={emp.infinityCode} value={emp.infinityCode}>
                                        {emp.infinityCode} - {emp.sr}
                                    </option>
                                ))}
                            </select>
                            <div style={{ fontSize: fonts.small, marginTop: '0.5rem', opacity: 0.7 }}>
                                {text[language].selectEmployeesHint}
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.gap }}>
                            <button onClick={() => {
                                setShowAssignModal(false);
                                setSelectedEmployeeIds([]);
                            }} style={styles.cancelBtn}>
                                {text[language].cancel}
                            </button>
                            <button onClick={handleAssignEmployees} style={styles.submitBtn} disabled={loading}>
                                {loading ? text[language].loading : text[language].assign}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GroupsManagement;